// frontend/src/components/wizard/PipelineGraph.jsx
import React, { useState, useMemo } from 'react';
import { X, Maximize2, GitMerge } from 'lucide-react';
import { useWizardStore } from '../../store/useWizardStore';
import { useLanguageStore } from '../../store/useLanguageStore';

// ══════════════════════════════════════════════════════════════
//  COLORES POR TIPO DE RELACIÓN
// ══════════════════════════════════════════════════════════════
const JOIN_COLOR = {
  INNER: '#6366f1',
  LEFT: '#f59e0b',
  RIGHT: '#10b981',
  FULL: '#a78bfa',
  FK: '#06b6d4',
  NONE: '#94a3b8',
};

// Motores de archivos planos — coherente con FLAT_TYPES del frontend
const FLAT_FILE_ENGINES = new Set(['csv', 'excel', 'googlesheets', 'dbf']);

// ══════════════════════════════════════════════════════════════
//  CONSTANTES DE LAYOUT DEL GRAFO SVG
// ══════════════════════════════════════════════════════════════
const GW = 540;
const NW = 130;
const NH = 40;
const NGAP = 18;
const SRCX = 54;
const TGTX = GW - 54 - NW;    // 356
const TGTRX = TGTX + NW;       // 486
const BUSSRC = SRCX + NW + 14;  // 198
const BUSTGT = TGTX - 14;       // 342
const ETLW = 60;
const ETLH = 30;
const ETLX = (GW - ETLW) / 2; // 240
const ETLRX = ETLX + ETLW;     // 300
const HDR = 40;  // encabezado: label + engine
const VPAD = 14;
const ARC = 28;
const MINIMAX = 4;

function syncLabel(mode, t) {
  const key = `wizard.pipeline_graph.sync_${mode}`;
  const fallback = { append: 'Insertar', upsert: 'Actualizar', overwrite: 'Reemplazar' };
  return t(key) || fallback[mode] || (mode || 'ETL').toUpperCase();
}

const svgH = (nSrc, nTgt) => HDR + VPAD + Math.max(nSrc, nTgt, 1) * NH + (Math.max(nSrc, nTgt, 1) - 1) * NGAP + VPAD;
const clTop = () => HDR + VPAD;
const nodeY = (i) => clTop() + i * (NH + NGAP);
const nodeCY = (i) => nodeY(i) + NH / 2;
const truncate = (s, n = 14) => s && s.length > n ? s.slice(0, n - 1) + '…' : (s || '');

// ══════════════════════════════════════════════════════════════
//  HELPER: etiquetas de display para un nodo
//
//  Motor DB       → { line1: tableName,          line2: null       }
//  googlesheets   → { line1: spreadsheetName,    line2: sheetName  }
//  excel          → { line1: workbookName,        line2: sheetName? }
//  csv / dbf      → { line1: fileName,            line2: null       }
//
//  Fallback gracioso: si spreadsheetName no existe en config,
//  muestra 'Google Sheets' como texto identificador del archivo.
// ══════════════════════════════════════════════════════════════
function getNodeLabel(table, engine, config) {
  const eng = engine?.toLowerCase();

  if (!FLAT_FILE_ENGINES.has(eng)) {
    return { line1: table, line2: null };
  }

  if (eng === 'googlesheets') {
    // host = spreadsheet ID (ej. "1gyVDS8q4d_mmkB7GGXc...")
    // El nombre legible puede venir de spreadsheetName, fileName o database
    const file = config?.spreadsheetName || config?.fileName || config?.database || 'Google Sheets';
    // En 1:1: table === config.selectedTable = nombre de la hoja
    // En N:M: table = hoja individual de sourceSelectedTables
    const sheet = table || config?.selectedTable || '';
    return { line1: file, line2: sheet };
  }

  if (eng === 'excel') {
    const workbook = config?.selectedTable || table;
    const sheet = config?.sheetName || null;
    return { line1: workbook, line2: sheet };
  }

  // csv, dbf: selectedTable = nombre del archivo
  return { line1: config?.selectedTable || table, line2: null };
}

// ══════════════════════════════════════════════════════════════
//  HELPER: columna de JOIN para una tabla origen
//
//  Campos reales del store:
//    { tableFrom, columnFrom, tableTo, columnTo, joinType, ... }
//  Confirmado en config_head.parameters de todos los pipelines.
//  Solo aplica en ORIGEN. FK destino: directiva de orden, sin columna.
// ══════════════════════════════════════════════════════════════
function getJoinColumn(tableName, relationships) {
  for (const rel of (relationships || [])) {
    if (rel.tableFrom === tableName && rel.columnFrom) return rel.columnFrom;
    if (rel.tableTo === tableName && rel.columnTo) return rel.columnTo;
  }
  return null;
}

// ══════════════════════════════════════════════════════════════
//  ÍCONO SVG DE DATOS (3 barras) — reemplaza Lucide dentro de SVG
// ══════════════════════════════════════════════════════════════
function DataIcon({ x, y, color }) {
  return (
    <g opacity="0.9">
      <rect x={x} y={y + 5} width={3} height={5} rx="1" fill={color} />
      <rect x={x + 4} y={y + 2} width={3} height={8} rx="1" fill={color} />
      <rect x={x + 8} y={y} width={3} height={10} rx="1" fill={color} />
    </g>
  );
}

// ══════════════════════════════════════════════════════════════
//  MARCADORES DE FLECHA
// ══════════════════════════════════════════════════════════════
function ArrowDefs() {
  return (
    <defs>
      {[
        { id: 'arr-emerald', color: '#10b981' },
        { id: 'arr-indigo', color: '#6366f1' },
      ].map(({ id, color }) => (
        <marker key={id} id={id}
          markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto"
        >
          <polygon points="0 0, 7 3.5, 0 7" fill={color} />
        </marker>
      ))}
    </defs>
  );
}

// ══════════════════════════════════════════════════════════════
//  NODO DE TABLA
//
//  Lógica de segunda línea (prioridad):
//    1. joinCol (DB con JOIN configurado)  → ".columna"
//    2. line2 de getNodeLabel (flat file)  → "▸ sheetName"
//    3. Sin segunda línea                  → tabla/archivo centrado
// ══════════════════════════════════════════════════════════════
function TableNode({ i, table, side, joinCol, engine, config, progress }) {
  const isSrc = side === 'src';
  const nx = isSrc ? SRCX : TGTX;
  const ny = nodeY(i);
  const accent = isSrc ? '#6366f1' : '#10b981';
  const txtColor = isSrc ? '#312e81' : '#064e3b';
  const secColor = isSrc ? '#a5b4fc' : '#6ee7b7';
  const border = isSrc ? '#c7d2fe' : '#a7f3d0';

  const { line1, line2 } = getNodeLabel(table, engine, config);

  const secondary = joinCol
    ? `.${truncate(joinCol, 13)}`
    : (line2 ? `▸ ${truncate(line2, 11)}` : null);

  const hasSecondary = !!secondary;

  return (
    <g>
      <rect x={nx} y={ny} width={NW} height={NH} rx="7"
        fill="white" stroke={border} strokeWidth="1.5" />
      {isSrc
        ? <rect x={nx} y={ny} width={4} height={NH} rx="2" fill={accent} />
        : <rect x={nx + NW - 4} y={ny} width={4} height={NH} rx="2" fill={accent} />
      }
      <DataIcon x={nx + 10} y={ny + (NH - 10) / 2} color={accent} />
      <text
        x={nx + 27}
        y={ny + NH / 2 - (hasSecondary ? 6 : 0) + 1}
        fontSize="10" fontWeight="700" fill={txtColor}
        dominantBaseline="middle"
      >
        {truncate(line1)}
      </text>
      {hasSecondary && (
        <text x={nx + 27} y={ny + NH / 2 + 9}
          fontSize="7.5" fontWeight="600" fill={secColor}
        >
          {secondary}
        </text>
      )}
      {/* Badge de progreso — solo si hay datos */}
      {progress && !isSrc && progress.total > 0 && (() => {
        const pct = progress.total === 0 ? 0 : progress.mapped / progress.total;
        const badgeColor = pct >= 1 ? '#10b981' : pct > 0 ? '#f59e0b' : '#ef4444';
        const badgeBg = pct >= 1 ? '#d1fae5' : pct > 0 ? '#fef3c7' : '#fee2e2';
        const bx = nx + NW - 38;
        const by = ny + 2;
        return (
          <g>
            <rect x={bx} y={by} width={34} height={13} rx={6.5} fill={badgeBg} />
            <text x={bx + 17} y={by + 10} textAnchor="middle" fontSize="7" fontWeight="800" fill={badgeColor}>
              {progress.mapped}/{progress.total}
            </text>
          </g>
        );
      })()}
      {progress && isSrc && progress.srcCount > 0 && (
        <g>
          <rect x={nx + NW - 28} y={ny + 2} width={24} height={13} rx={6.5} fill="#e0e7ff" />
          <text x={nx + NW - 16} y={ny + 12} textAnchor="middle" fontSize="7" fontWeight="800" fill="#6366f1">
            {progress.srcCount}
          </text>
        </g>
      )}
    </g>
  );
}

// ══════════════════════════════════════════════════════════════
//  GRAFO SVG COMPLETO (usado en el modal)
//
//  srcLabel / tgtLabel: strings ya traducidos desde el padre
//  srcConfig / tgtConfig: source.config / target.config del store
// ══════════════════════════════════════════════════════════════
function PipelineGraphSVG({
  srcTables, tgtTables, srcRels, tgtRels,
  srcEngine, tgtEngine,
  srcConfig, tgtConfig,
  srcLabel, tgtLabel,
  syncMode, tableProgress, t,
}) {
  const H = svgH(srcTables.length, tgtTables.length);
  const maxN = Math.max(srcTables.length, tgtTables.length, 1);
  const clusterH = maxN * NH + (maxN - 1) * NGAP;
  const etlMidY = clTop() + clusterH / 2;
  const etlY = etlMidY - ETLH / 2;
  const hasSrc = srcTables.length > 0;
  const hasTgt = tgtTables.length > 0;
  const hasBoth = hasSrc && hasTgt;

  const srcCYs = srcTables.map((_, i) => nodeCY(i));
  const tgtCYs = tgtTables.map((_, i) => nodeCY(i));
  const srcBusTop = srcCYs[0] ?? etlMidY;
  const srcBusBot = srcCYs[srcCYs.length - 1] ?? etlMidY;
  const tgtBusTop = tgtCYs[0] ?? etlMidY;
  const tgtBusBot = tgtCYs[tgtCYs.length - 1] ?? etlMidY;

  const srcEdges = useMemo(() => (srcRels || []).map(rel => {
    const fi = srcTables.indexOf(rel.tableFrom);
    const ti = srcTables.indexOf(rel.tableTo);
    if (fi < 0 || ti < 0) return null;
    const x = SRCX, y1 = nodeCY(fi), y2 = nodeCY(ti), cx = x - ARC;
    return {
      path: `M ${x} ${y1} C ${cx} ${y1} ${cx} ${y2} ${x} ${y2}`,
      lx: cx, ly: (y1 + y2) / 2,
      label: rel.joinType || 'JOIN',
      color: JOIN_COLOR[rel.joinType] || '#6366f1',
    };
  }).filter(Boolean), [srcRels, srcTables]);

  const tgtEdges = useMemo(() => (tgtRels || []).filter(r => r.joinType !== 'NONE').map(rel => {
    const fi = tgtTables.indexOf(rel.tableFrom);
    const ti = tgtTables.indexOf(rel.tableTo);
    if (fi < 0 || ti < 0) return null;
    const x = TGTRX, y1 = nodeCY(fi), y2 = nodeCY(ti), cx = x + ARC;
    return {
      path: `M ${x} ${y1} C ${cx} ${y1} ${cx} ${y2} ${x} ${y2}`,
      lx: cx, ly: (y1 + y2) / 2,
      label: rel.joinType || 'FK',
      color: JOIN_COLOR[rel.joinType] || '#06b6d4',
    };
  }).filter(Boolean), [tgtRels, tgtTables]);

  return (
    <svg width={GW} height={H} viewBox={`0 0 ${GW} ${H}`}
      className="block overflow-visible" aria-label="Pipeline ETL graph"
    >
      <ArrowDefs />

      {/* Encabezados — label i18n + engine */}
      {hasSrc && (
        <>
          <text x={SRCX + NW / 2} y={HDR - 22}
            textAnchor="middle" fontSize="8" fontWeight="800" fill="#818cf8" letterSpacing="1.5"
          >{srcLabel}</text>
          {srcEngine && (
            <text x={SRCX + NW / 2} y={HDR - 8}
              textAnchor="middle" fontSize="8" fontWeight="600" fill="#a5b4fc" letterSpacing="0.5"
            >{srcEngine.toUpperCase()}</text>
          )}
        </>
      )}
      {hasTgt && (
        <>
          <text x={TGTX + NW / 2} y={HDR - 22}
            textAnchor="middle" fontSize="8" fontWeight="800" fill="#34d399" letterSpacing="1.5"
          >{tgtLabel}</text>
          {tgtEngine && (
            <text x={TGTX + NW / 2} y={HDR - 8}
              textAnchor="middle" fontSize="8" fontWeight="600" fill="#6ee7b7" letterSpacing="0.5"
            >{tgtEngine.toUpperCase()}</text>
          )}
        </>
      )}

      {/* Arcos de JOINs (izquierda del origen) */}
      {srcEdges.map((e, i) => (
        <g key={`se-${i}`}>
          <path d={e.path} fill="none" stroke={e.color} strokeWidth="1.5" opacity="0.75" />
          <rect x={e.lx - 15} y={e.ly - 7} width="30" height="14" rx="4" fill={e.color} opacity="0.12" />
          <rect x={e.lx - 15} y={e.ly - 7} width="30" height="14" rx="4" fill="none" stroke={e.color} strokeWidth="0.7" opacity="0.5" />
          <text x={e.lx} y={e.ly + 4} textAnchor="middle" fontSize="6.5" fontWeight="800" fill={e.color}>{e.label.slice(0, 5)}</text>
        </g>
      ))}

      {/* Arcos de FKs (derecha del destino) */}
      {tgtEdges.map((e, i) => (
        <g key={`te-${i}`}>
          <path d={e.path} fill="none" stroke={e.color} strokeWidth="1.5" opacity="0.75" />
          <rect x={e.lx - 15} y={e.ly - 7} width="30" height="14" rx="4" fill={e.color} opacity="0.12" />
          <rect x={e.lx - 15} y={e.ly - 7} width="30" height="14" rx="4" fill="none" stroke={e.color} strokeWidth="0.7" opacity="0.5" />
          <text x={e.lx} y={e.ly + 4} textAnchor="middle" fontSize="6.5" fontWeight="800" fill={e.color}>{e.label.slice(0, 5)}</text>
        </g>
      ))}

      {/* Bus origen */}
      {hasSrc && srcTables.length > 1 && (
        <line x1={BUSSRC} y1={srcBusTop} x2={BUSSRC} y2={srcBusBot} stroke="#c7d2fe" strokeWidth="2" />
      )}
      {srcTables.map((_, i) => (
        <line key={`st-${i}`} x1={SRCX + NW} y1={nodeCY(i)} x2={BUSSRC} y2={nodeCY(i)} stroke="#c7d2fe" strokeWidth="1.5" />
      ))}

      {/* Bus origen → ETL → Bus destino */}
      {hasBoth && (
        <>
          <line x1={BUSSRC} y1={etlMidY} x2={ETLX} y2={etlMidY} stroke="#6366f1" strokeWidth="2" />
          <line x1={ETLRX} y1={etlMidY} x2={BUSTGT} y2={etlMidY} stroke="#10b981" strokeWidth="2" markerEnd="url(#arr-emerald)" />
        </>
      )}

      {/* Caja ETL */}
      {hasBoth && (
        <g>
          <rect x={ETLX} y={etlY} width={ETLW} height={ETLH} rx="9" fill="#4f46e5" />
          <rect x={ETLX} y={etlY} width={ETLW} height={ETLH} rx="9" fill="none" stroke="#818cf8" strokeWidth="1" opacity="0.6" />
          <text x={ETLX + ETLW / 2} y={etlY + ETLH / 2 - 2}
            textAnchor="middle" fontSize="7" fontWeight="700" fill="white" opacity="0.7" letterSpacing="0.5"
          >ETL</text>
          <text x={ETLX + ETLW / 2} y={etlY + ETLH / 2 + 9}
            textAnchor="middle" fontSize="7.5" fontWeight="900" fill="white" letterSpacing="0.5"
          >{syncLabel(syncMode, t)}</text>
        </g>
      )}

      {/* Bus destino */}
      {hasTgt && tgtTables.length > 1 && (
        <line x1={BUSTGT} y1={tgtBusTop} x2={BUSTGT} y2={tgtBusBot} stroke="#a7f3d0" strokeWidth="2" />
      )}
      {tgtTables.map((_, i) => (
        <line key={`tt-${i}`} x1={BUSTGT} y1={nodeCY(i)} x2={TGTX} y2={nodeCY(i)} stroke="#a7f3d0" strokeWidth="1.5" markerEnd="url(#arr-emerald)" />
      ))}

      {/* Nodos ORIGEN */}
      {srcTables.map((tbl, i) => (
        <TableNode key={`sn-${i}`} i={i} table={tbl} side="src"
          joinCol={getJoinColumn(tbl, srcRels)}
          engine={srcEngine} config={srcConfig}
          progress={tableProgress?.[tbl]}
        />
      ))}

      {/* Nodos DESTINO — joinCol=null: FK es directiva de orden (Regla 11) */}
      {tgtTables.map((tbl, i) => (
        <TableNode key={`tn-${i}`} i={i} table={tbl} side="tgt"
          joinCol={null}
          engine={tgtEngine} config={tgtConfig}
          progress={tableProgress?.[tbl]}
        />
      ))}
    </svg>
  );
}

// ══════════════════════════════════════════════════════════════
//  BADGE de tabla para el minimap
//  Usa getNodeLabel para mostrar archivo/hoja en flat files
// ══════════════════════════════════════════════════════════════
function TableBadge({ table, engine, config, side, progress }) {
  const isSrc = side === 'src';
  const { line1, line2 } = getNodeLabel(table, engine, config);
  const displayText = line2
    ? `${truncate(line1, 7)} / ${truncate(line2, 7)}`
    : truncate(line1, 12);
  const pct = progress?.total ? progress.mapped / progress.total : -1;

  return (
    <div
      className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[9px] font-bold truncate max-w-full ${isSrc
        ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
        : 'bg-emerald-50 border-emerald-200 text-emerald-700'
        }`}
      title={line2 ? `${line1} / ${line2}` : line1}
    >
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isSrc ? 'bg-indigo-400'
        : pct >= 1 ? 'bg-emerald-500'
          : pct > 0 ? 'bg-amber-400'
            : pct === 0 ? 'bg-red-400'
              : 'bg-emerald-400'
        }`} />
      <span className="truncate">{displayText}</span>
    </div>
  );
}

function MoreBadge({ count, side }) {
  return (
    <div className={`text-[8px] font-bold px-1.5 py-0.5 rounded-md ${side === 'src'
      ? 'text-indigo-500 bg-indigo-50 border border-indigo-100'
      : 'text-emerald-500 bg-emerald-50 border border-emerald-100'
      }`}>
      +{count} más
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  MINIMAP (sidebar desktop — CSS, no SVG)
// ══════════════════════════════════════════════════════════════
function MiniMap({
  srcTables, tgtTables,
  srcEngine, tgtEngine,
  srcConfig, tgtConfig,
  srcLabel, tgtLabel,
  syncMode, tableProgress,
  onClick, t,
}) {
  const srcDisplay = srcTables.slice(0, MINIMAX);
  const tgtDisplay = tgtTables.slice(0, MINIMAX);
  const srcOverflow = srcTables.length - MINIMAX;
  const tgtOverflow = tgtTables.length - MINIMAX;

  return (
    <div
      role="button" tabIndex={0}
      className="group cursor-pointer rounded-xl border border-slate-200 bg-white p-3 hover:border-indigo-200 hover:shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-indigo-300"
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      title={t('wizard.pipeline_graph.expand') || 'Ver pipeline completo'}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
          <GitMerge className="w-3 h-3" />
          {t('wizard.pipeline_graph.title') || 'Pipeline'}
        </span>
        <Maximize2 className="w-3 h-3 text-slate-400 group-hover:text-indigo-500 transition-colors" />
      </div>

      <div className="flex items-start gap-1.5 min-w-0">

        {/* Columna origen */}
        {srcTables.length > 0 && (
          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
            <div className="mb-1">
              <div className="text-[7px] font-bold text-indigo-400 uppercase tracking-wide leading-tight">
                {srcLabel}
              </div>
              {srcEngine && (
                <div className="text-[7px] font-semibold text-indigo-300 leading-tight -mt-px">
                  {srcEngine.toUpperCase()}
                </div>
              )}
            </div>
            {srcDisplay.map(tbl => (
              <TableBadge key={tbl} table={tbl} engine={srcEngine} config={srcConfig} side="src" progress={tableProgress?.[tbl]} />
            ))}
            {srcOverflow > 0 && <MoreBadge count={srcOverflow} side="src" />}
          </div>
        )}

        {/* Flecha ETL */}
        {srcTables.length > 0 && tgtTables.length > 0 && (
          <div className="flex flex-col items-center justify-center pt-3 shrink-0 gap-0.5">
            <div className="flex items-center gap-px">
              <div className="w-3 h-px bg-slate-300" />
              <div className="w-0 h-0 border-t-[3px] border-t-transparent border-b-[3px] border-b-transparent border-l-[5px] border-l-slate-400" />
            </div>
            <span className="text-[7px] text-slate-400 font-bold">
              {syncLabel(syncMode, t)}
            </span>
          </div>
        )}

        {/* Columna destino */}
        {tgtTables.length > 0 && (
          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
            <div className="mb-1">
              <div className="text-[7px] font-bold text-emerald-400 uppercase tracking-wide leading-tight">
                {tgtLabel}
              </div>
              {tgtEngine && (
                <div className="text-[7px] font-semibold text-emerald-300 leading-tight -mt-px">
                  {tgtEngine.toUpperCase()}
                </div>
              )}
            </div>
            {tgtDisplay.map(tbl => (
              <TableBadge key={tbl} table={tbl} engine={tgtEngine} config={tgtConfig} side="tgt" progress={tableProgress?.[tbl]} />
            ))}
            {tgtOverflow > 0 && <MoreBadge count={tgtOverflow} side="tgt" />}
          </div>
        )}

        {srcTables.length > 0 && tgtTables.length === 0 && (
          <div className="text-[8px] text-slate-400 italic pt-3">
            {t('wizard.pipeline_graph.no_target') || 'Sin destino aún'}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  LEYENDA DE TIPOS DE RELACIÓN (solo muestra los usados)
// ══════════════════════════════════════════════════════════════
const LEGEND_ITEMS = [
  { key: 'INNER', label: 'INNER JOIN' },
  { key: 'LEFT', label: 'LEFT JOIN' },
  { key: 'RIGHT', label: 'RIGHT JOIN' },
  { key: 'FULL', label: 'FULL JOIN' },
  { key: 'FK', label: 'FK (padre→hijo)' },
];

function GraphLegend({ srcRels, tgtRels, t }) {
  const usedTypes = new Set([
    ...(srcRels || []).map(r => r.joinType),
    ...(tgtRels || []).filter(r => r.joinType !== 'NONE').map(r => r.joinType),
  ]);
  const items = LEGEND_ITEMS.filter(i => usedTypes.has(i.key));
  if (items.length === 0) return null;
  return (
    <div className="px-6 py-2.5 border-t border-slate-100 bg-slate-50 shrink-0 flex items-center gap-3 flex-wrap">
      <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wide">
        {t('wizard.pipeline_graph.legend') || 'Relaciones'}
      </span>
      {items.map(({ key, label }) => (
        <div key={key} className="flex items-center gap-1">
          <svg width="20" height="3" className="shrink-0">
            <line x1="0" y1="1.5" x2="20" y2="1.5" stroke={JOIN_COLOR[key]} strokeWidth="2" />
          </svg>
          <span className="text-[9px] text-slate-500 font-mono">{label}</span>
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  MODAL FULLSCREEN
// ══════════════════════════════════════════════════════════════
function GraphModal({
  onClose,
  srcTables, tgtTables, srcRels, tgtRels,
  srcEngine, tgtEngine,
  srcConfig, tgtConfig,
  srcLabel, tgtLabel,
  syncMode, tableProgress,
  t,
}) {
  React.useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50 shrink-0">
          <div>
            <h3 className="font-bold text-slate-800 flex items-center gap-2 text-sm">
              <GitMerge className="w-4 h-4 text-indigo-600" />
              {t('wizard.pipeline_graph.title') || 'Pipeline ETL'}
            </h3>
            <p className="text-[11px] text-slate-400 mt-0.5">
              <span className="font-semibold text-indigo-500">{srcTables.length}</span>
              {' '}{t('wizard.pipeline_graph.sources') || 'tabla(s) origen'}
              {' · '}
              <span className="font-semibold text-emerald-500">{tgtTables.length}</span>
              {' '}{t('wizard.pipeline_graph.targets') || 'tabla(s) destino'}
            </p>
          </div>
          <button onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 rounded-full hover:bg-slate-100 transition"
            title={t('wizard.close') || 'Cerrar'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Grafo — overflow-x-auto para scroll horizontal en móvil (SVG = 540px) */}
        <div className="flex-1 overflow-auto p-6 flex items-start justify-center bg-slate-50/50">
          <div className="overflow-x-auto">
            <PipelineGraphSVG
              srcTables={srcTables} tgtTables={tgtTables}
              srcRels={srcRels} tgtRels={tgtRels}
              srcEngine={srcEngine} tgtEngine={tgtEngine}
              srcConfig={srcConfig} tgtConfig={tgtConfig}
              srcLabel={srcLabel} tgtLabel={tgtLabel}
              syncMode={syncMode} tableProgress={tableProgress} t={t}
            />
          </div>
        </div>

        <GraphLegend srcRels={srcRels} tgtRels={tgtRels} t={t} />

        {/* Footer */}
        <div className="px-6 py-2.5 border-t border-slate-100 bg-white shrink-0 flex items-center justify-between">
          <span className="text-[10px] text-slate-400">
            {t('wizard.pipeline_graph.esc_hint') || 'ESC o clic fuera para cerrar'}
          </span>
          <button onClick={onClose}
            className="text-[10px] text-indigo-500 hover:text-indigo-700 font-semibold transition-colors"
          >
            {t('wizard.close') || 'Cerrar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  COMPONENTE PRINCIPAL EXPORTADO
//  Regla 13: montado en Migration.jsx después de </nav>
// ══════════════════════════════════════════════════════════════
export function PipelineGraph() {
  const [modalOpen, setModalOpen] = useState(false);
  const { t } = useLanguageStore();

  const {
    sourceSelectedTables, targetSelectedTables,
    sourceRelationships, targetRelationships,
    source, target,
    mapping, syncMode,
    sourceColumns, targetColumns,
  } = useWizardStore();

  // ── Progreso de mapeo por tabla (target-centric) ─────────────────
  const tableProgress = useMemo(() => {
    const progress = {};
    const tCols = targetColumns || [];
    const maps = mapping || [];

    // Contar columnas totales por tabla destino
    for (const col of tCols) {
      const table = col.name.includes('.')
        ? col.name.split('.')[0]
        : (targetSelectedTables?.[0] || target?.config?.selectedTable || '_tgt');
      progress[table] = progress[table] || { mapped: 0, total: 0 };
      progress[table].total++;
    }

    // Contar mapeadas por tabla destino
    for (const m of maps) {
      if (!m.target || m.type === 'unmapped') continue;
      const table = m.target.includes('.')
        ? m.target.split('.')[0]
        : (targetSelectedTables?.[0] || target?.config?.selectedTable || '_tgt');
      if (progress[table]) progress[table].mapped++;
    }

    // Columnas source por tabla (solo conteo informativo)
    for (const col of (sourceColumns || [])) {
      const table = col.name.includes('.')
        ? col.name.split('.')[0]
        : (sourceSelectedTables?.[0] || source?.config?.selectedTable || '_src');
      if (!progress[table]) progress[table] = { mapped: 0, total: 0 };
      if (!progress[table].srcCount) progress[table].srcCount = 0;
      progress[table].srcCount++;
    }

    return progress;
  }, [mapping, targetColumns, sourceColumns, sourceSelectedTables, targetSelectedTables, source, target]);

  const srcTables = sourceSelectedTables?.length > 0
    ? sourceSelectedTables
    : (source?.config?.selectedTable ? [source.config.selectedTable] : []);

  const tgtTables = targetSelectedTables?.length > 0
    ? targetSelectedTables
    : (target?.config?.selectedTable ? [target.config.selectedTable] : []);

  // source.type es el campo canónico (lowercase) — confirmado en todos los pipelines
  const srcEngine = source?.type || source?.config?.engineType || null;
  const tgtEngine = target?.type || target?.config?.engineType || null;

  // config completo para getNodeLabel (spreadsheetName, sheetName, etc.)
  const srcConfig = source?.config || null;
  const tgtConfig = target?.config || null;

  // Labels i18n computados aquí — el SVG recibe strings, no la función t()
  const srcLabel = t('wizard.pipeline_graph.source') || 'Origen';
  const tgtLabel = t('wizard.pipeline_graph.target') || 'Destino';

  if (srcTables.length === 0 && tgtTables.length === 0) return null;

  const sharedProps = {
    srcTables, tgtTables,
    srcEngine, tgtEngine,
    srcConfig, tgtConfig,
    srcLabel, tgtLabel,
    syncMode: syncMode || 'append',
    tableProgress,
  };

  return (
    <>
      {/* Minimap desktop */}
      <div className="hidden md:block mt-6 pt-6 border-t border-slate-200">
        <MiniMap
          {...sharedProps}
          onClick={() => setModalOpen(true)}
          t={t}
        />
      </div>

      {/* FAB mobile */}
      <button
        onClick={() => setModalOpen(true)}
        className="md:hidden fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-full shadow-lg hover:bg-indigo-700 active:scale-95 transition-all"
        aria-label={t('wizard.pipeline_graph.title') || 'Ver pipeline'}
      >
        <GitMerge className="w-4 h-4" />
        <span className="text-sm font-bold">
          {t('wizard.pipeline_graph.fab') || 'Pipeline'}
        </span>
        {(srcTables.length + tgtTables.length) > 2 && (
          <span className="bg-white/20 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
            {srcTables.length + tgtTables.length}
          </span>
        )}
      </button>

      {/* Modal */}
      {modalOpen && (
        <GraphModal
          {...sharedProps}
          srcRels={sourceRelationships}
          tgtRels={targetRelationships}
          onClose={() => setModalOpen(false)}
          t={t}
        />
      )}
    </>
  );
}
